import React from 'react';
import { Tooltip } from 'antd';
import { FT170 } from '../../../services/t207Api';
import { Mode } from '../hooks/useT207Mode';

interface T207Permissions {
  canView: boolean;
  canCreate: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canExport: boolean;
  canPrint: boolean;
  canApprove: boolean;
  hasAnyPermission: boolean;
}

interface ActionBarProps {
  mode: Mode;
  isEditing: boolean;
  record: FT170 | null;
  currentIdx: number;
  allIdsLength: number;
  permissions: T207Permissions;
  onAdd: () => void;
  onModify: () => void;
  onDelete: () => void;
  onPrint: () => void;
  onNavTo: (idx: number) => void;
  onExit: () => void;
}

/** PermBtn: permission-guarded button */
const PermBtn: React.FC<{
  allowed: boolean;
  disabled: boolean;
  className?: string;
  title?: string;
  onClick?: () => void;
  children: React.ReactNode;
}> = ({ allowed, disabled, className, title, onClick, children }) => {
  const btn = (
    <button
      className={className || 'btn'}
      disabled={disabled || !allowed}
      title={title}
      onClick={onClick}
    >
      {children}
    </button>
  );
  if (!allowed) {
    return <Tooltip title="權限不足">{btn}</Tooltip>;
  }
  return btn;
};

const ActionBar: React.FC<ActionBarProps> = ({
  mode, isEditing, record,
  currentIdx, allIdsLength,
  permissions,
  onAdd, onModify, onDelete, onPrint, onNavTo, onExit,
}) => {
  const { canView, canCreate, canEdit, canDelete, canPrint } = permissions;
  const canSave = mode === 'add' ? canCreate : canEdit;

  return (
    <div className="action-bar">
      {/* Navigation */}
      <div className="action-group">
        <PermBtn allowed={canView} disabled={isEditing || currentIdx <= 0} onClick={() => onNavTo(0)}>最前</PermBtn>
        <PermBtn allowed={canView} disabled={isEditing || currentIdx <= 0} onClick={() => onNavTo(currentIdx - 1)}>上一筆</PermBtn>
        <PermBtn allowed={canView} disabled={isEditing || currentIdx >= allIdsLength - 1} onClick={() => onNavTo(currentIdx + 1)}>下一筆</PermBtn>
        <PermBtn allowed={canView} disabled={isEditing || currentIdx >= allIdsLength - 1} onClick={() => onNavTo(allIdsLength - 1)}>最後</PermBtn>
      </div>

      {/* CRUD */}
      <div className="action-group">
        {/* CMDADD: 新增 / 存檔 */}
        <PermBtn
          allowed={isEditing ? canSave : canCreate}
          disabled={false}
          className="btn btn-primary"
          onClick={onAdd}
        >
          {isEditing ? '存檔' : '新增'}
        </PermBtn>
        {/* CMDMODI: 修改 / 還原 */}
        <PermBtn
          allowed={isEditing ? true : canEdit}
          disabled={!isEditing && !record}
          onClick={onModify}
        >
          {isEditing ? '還原' : '修改'}
        </PermBtn>
        {/* CMDDEL */}
        <PermBtn
          allowed={canDelete}
          disabled={isEditing || !record}
          className="btn btn-danger"
          onClick={onDelete}
        >
          刪除
        </PermBtn>

        <div className="action-separator" />

        {/* 列印 */}
        <PermBtn allowed={canPrint} disabled={isEditing || !record} onClick={onPrint}>列印</PermBtn>

        {/* 離開 */}
        <button className="btn" onClick={onExit}>離開</button>
      </div>
    </div>
  );
};

export default React.memo(ActionBar);
